import { PerformanceLabel } from './mutual-fund-detail.types'
import { CategoryIconProps } from '../icons/CategorySvgIcon'

export enum FundCategory {
  LARGE_CAP = 'LARGE_CAP',
  MID_CAP = 'MID_CAP',
  SMALL_CAP = 'SMALL_CAP'
}

/**
 * Fund Nature enum - categorizes funds into Equity, Index, or Hybrid
 */
export enum FundNature {
  EQUITY = 'EQUITY',
  DEBT = 'DEBT',
  SOLUTIONS_ORIENTED = 'SOLUTIONS_ORIENTED',
  INDEX = 'INDEX',
  HYBRID = 'HYBRID',
  OTHERS = 'OTHERS',
  COMMODITY = 'COMMODITY',
  GLOBAL = 'GLOBAL',
  FUND_OF_FUNDS = 'FUND_OF_FUNDS'
}

/**
 * Browse Categories Tab IDs
 */
export enum BrowseTabId {
  EQUITY_FUNDS = 'EQUITY_FUNDS',
  DEBT_FUNDS = 'DEBT_FUNDS',
  INDEX_FUNDS = 'INDEX_FUNDS',
  HYBRID_FUNDS = 'HYBRID_FUNDS',
  COMMODITY_FUNDS = 'COMMODITY_FUNDS',
  SOLUTIONS_ORIENTED_FUNDS = 'SOLUTIONS_ORIENTED_FUNDS',
  GLOBAL_FUNDS = 'GLOBAL_FUNDS',
  FUND_OF_FUNDS = 'FUND_OF_FUNDS'
}

/**
 * Hybrid Fund Category IDs
 */
export const HYBRID_CATEGORY_IDS = [
  'AGGRESSIVE_ALLOCATION',
  'BALANCED_ALLOCATION',
  'CONSERVATIVE_ALLOCATION',
  'DYNAMIC_ASSET_ALLOCATION',
  'EQUITY_SAVINGS',
  'MULTI_ASSET_ALLOCATION',
  'ARBITRAGE_FUND'
] as const

export interface ReturnsMetrics {
  metricsType: 'ROLLING_RETURNS' | 'TRAILING_RETURNS' | 'AUM_CHANGE' | 'AUM' | 'AGE'
  tenureInYears?: number
  tenurePeriod?: 'YEAR' | 'MONTH'
  percentageReturn: number
  returnRank?: number
  performanceLabel: PerformanceLabel
}

export interface FinancialMetrics {
  metricsType: 'ROLLING_RETURNS' | 'TRAILING_RETURNS' | 'AUM_CHANGE' | 'AUM' | 'AGE'
  tenure: {
    value: number
    period: 'YEAR' | 'MONTH' | 'DAY' | 'ALL'
  }
  value: {
    amount: number
    unit: 'PERCENTAGE' | 'RUPEES'
  }
  rank: number
  performanceLabel: PerformanceLabel
}

export interface FundsPowerResponse {
  isin: string
  name: string
  category: string
  financialMetrics: FinancialMetrics[]
  powerPerformance: 'IN_FORM' | 'ON_TRACK' | 'OFF_TRACK' | 'OUT_OF_FORM' | 'PERFORMANCE_LOCKED' | 'NOT_RATED'
  recency: number
  powerRank: number
  isUsersFund: boolean
  recencyScore: number
  consistencyScore: number
  volatilityScore: number
  recencyPerformanceTag: PerformanceLabel
  consistencyPerformanceTag: PerformanceLabel
  volatilityPerformanceTag: PerformanceLabel
}

export interface FundsData {
  fundsPowerResponseList: FundsPowerResponse[]
  fundsCount: number
  isUserElite: boolean
  isSubscribedToPeerAnalyser: boolean
  isUserExhaustedPASubs: boolean
  selectedFundsIsinList: string[]
  userFundsIsinList: string[]
  isFtuxEnabled: boolean
  tenureInDays: number
  throughFamilyAccess: boolean
  secondaryUsername: string
  primaryUserName: string
  peerAnalyserDisplayFilters: string[]
}

// Browse Categories API types
export interface CategorySection {
  categoryTitle: string
  categoryType: string[]
}

export interface BrowseCategoriesResponse {
  indexFundsList: CategorySection[]
  equityFundsList: CategorySection[]
  debtFundsList?: CategorySection[]
  hybridFundsList: CategorySection[]
  commodityFundsList: CategorySection[]
  solutionOrientedFundsList: CategorySection[]
  globalFundsList: CategorySection[]
  fofFundsList?: CategorySection[]
  paSubscribedCategories: string[]
  userFundCategories: string[]
}

// Browse Categories UI types
export interface BrowseCategoryItem {
  id: string
  label: string
  description: string
  Icon: React.FC<CategoryIconProps>
}

export interface BrowseCategorySection {
  id: string
  title: string
  categories: BrowseCategoryItem[]
}

export interface BrowseTabData {
  id: BrowseTabId | string
  label: string
  sections: BrowseCategorySection[]
}
